import React, { useState, useEffect } from 'react';
import { education, firstClassDegree } from '../data';
import './Education.css';
import './Experience.css';

/* ── Clean Inline SVG Icons (Executive Style - No Emojis) ──── */
const IconArrowUpRight = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="7" y1="17" x2="17" y2="7"></line>
    <polyline points="7 7 17 7 17 17"></polyline>
  </svg>
);

const IconDocument = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
  </svg>
);

const IconMaximize = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="15 3 21 3 21 9"></polyline>
    <polyline points="9 21 3 21 3 15"></polyline>
    <line x1="21" y1="3" x2="14" y2="10"></line>
    <line x1="3" y1="21" x2="10" y2="14"></line>
  </svg>
);

const IconStar = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="1"
    aria-hidden="true"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
  </svg>
);

const IconClose = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

/* ── Lightbox Modal for Degree Document ──────────────────── */
const DegreeLightbox = ({ image, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!image) return null;

  return (
    <div className="comp-lightbox-overlay" onClick={onClose}>
      <div className="comp-lightbox-header" onClick={(e) => e.stopPropagation()}>
        <div className="comp-lightbox-title">
          BSc (Hons) Software Engineering — Official Registration & Award Status
        </div>
        <button
          type="button"
          className="comp-lightbox-close"
          onClick={onClose}
          aria-label="Close Lightbox"
        >
          <IconClose />
        </button>
      </div>

      <div className="comp-lightbox-body" onClick={(e) => e.stopPropagation()}>
        <img
          src={image}
          alt="First Class Honours Degree Certificate — University of Bedfordshire"
          className="comp-lightbox-img"
        />
      </div>

      <div className="comp-lightbox-footer" onClick={(e) => e.stopPropagation()}>
        <div className="comp-lightbox-caption">
          University of Bedfordshire (UK) in collaboration with SLIIT Northern UNI, Jaffna · Awarded 5 October 2026 · First Class
        </div>
        <a
          href={image}
          target="_blank"
          rel="noopener noreferrer"
          className="comp-lightbox-download"
        >
          <span>Open Full Resolution Document</span>
          <IconArrowUpRight />
        </a>
      </div>
    </div>
  );
};

/* ── Main Education Component ────────────────────────────── */
const Education = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const degree = firstClassDegree;

  return (
    <section id="education" className="sec edu-sec">
      <div className="edu-ambient-glow" />

      {/* Section Header */}
      <div className="sec-inner" style={{ position: 'relative', zIndex: 1 }}>
        <div className="sec-label">01.5 — Academic Excellence & Degree</div>
        <h2 className="sec-title">
          First Class Honours<br />
          <em>BSc (Hons) Software Engineering</em>
        </h2>
        <p className="sec-desc" style={{ marginTop: '0.5rem' }}>
          Officially completed and awarded by the <strong>University of Bedfordshire (United Kingdom)</strong> in collaboration with <strong>SLIIT Northern UNI, Jaffna</strong>.
        </p>
      </div>

      {/* ── First Class Honours Presidential Showcase ───────── */}
      <div className="edu-firstclass-card reveal">
        <div className="edu-firstclass-inner">
          {/* Left Column: Academic Credentials & Story */}
          <div className="edu-info-col">
            <div className="edu-top-meta">
              <div className="edu-top-logos">
                <img
                  src={degree.awardingBodyLogo}
                  alt="University of Bedfordshire"
                  className="edu-uni-logo"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <img
                  src={degree.collaborativeLogo}
                  alt="SLIIT"
                  className="edu-uni-logo"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
              <div className="edu-badge-firstclass">
                <IconStar />
                <span>First Class Honours · Full Award</span>
              </div>
            </div>

            <div className="edu-eyebrow">HIGHEST ACADEMIC DISTINCTION</div>
            <h3 className="edu-title">
              BSc (Hons) in<br />
              <em>Software Engineering</em>
            </h3>

            <div className="edu-institution-tag">
              <span>Awarded by:</span>
              <strong>University of Bedfordshire (UK)</strong>
              <span>· Collaborative Partner:</span>
              <strong>SLIIT Northern UNI, Jaffna</strong>
            </div>

            {/* Official Metadata Grid */}
            <div className="edu-meta-grid">
              <div className="edu-meta-cell">
                <span className="edu-meta-lbl">Classification</span>
                <span className="edu-meta-val gold">First Class</span>
              </div>
              <div className="edu-meta-cell">
                <span className="edu-meta-lbl">Award Date</span>
                <span className="edu-meta-val">05 Oct 2026</span>
              </div>
              <div className="edu-meta-cell">
                <span className="edu-meta-lbl">Qualification</span>
                <span className="edu-meta-val">BSc (Hons) L6</span>
              </div>
              <div className="edu-meta-cell">
                <span className="edu-meta-lbl">Status</span>
                <span className="edu-meta-val">Full Award</span>
              </div>
            </div>

            {/* Personal Story Quote Box */}
            <div className="edu-story-box">
              <div className="edu-story-header">
                <IconStar />
                <span className="edu-story-tag">{degree.story.title}</span>
              </div>
              <p className="edu-story-quote">
                “{degree.story.quote}”
              </p>
              <p className="edu-story-narrative">
                {degree.story.narrative}
              </p>
            </div>

            {/* CTAs */}
            <div className="edu-actions">
              <button
                type="button"
                className="edu-btn-gold"
                onClick={() => setModalOpen(true)}
              >
                <IconDocument />
                <span>Inspect Official Degree Document</span>
                <IconArrowUpRight />
              </button>
              <a href="#research" className="edu-btn-ghost">
                <span>View Final-Year Research</span>
                <IconArrowUpRight />
              </a>
            </div>
          </div>

          {/* Right Column: Official Degree Document Viewer */}
          <div className="edu-doc-col">
            <div
              className="edu-doc-frame"
              onClick={() => setModalOpen(true)}
              title="Click to view full-resolution official document"
            >
              <img
                src={degree.documentImage}
                alt="Registration Status & Award — University of Bedfordshire"
                className="edu-doc-img"
              />
              <div className="edu-doc-zoom-overlay">
                <IconMaximize />
                <span>Click for Fullscreen</span>
              </div>
            </div>
            <div className="edu-doc-caption">
              Official University of Bedfordshire Registration Status & Award Record
            </div>
          </div>
        </div>
      </div>

      {/* ── Wall of Gratitude (The People Behind This Journey) ── */}
      <div className="edu-gratitude-section reveal">
        <div className="edu-gratitude-shell">
          <div className="edu-gratitude-header">
            <span className="edu-gratitude-kicker">ACKNOWLEDGEMENTS & MENTORSHIP</span>
            <h3 className="edu-gratitude-title">The People Behind This Journey</h3>
            <p className="edu-gratitude-subtitle">
              “When I look back, I realise I could not have reached this point by myself. This achievement may have my name on it, but there are so many people behind it.”
            </p>
          </div>

          {/* Research Supervisor Spotlight */}
          <div className="edu-supervisor-card">
            <div className="edu-supervisor-top">
              <div className="edu-supervisor-meta">
                <span className="edu-supervisor-name">{degree.supervisor.name}</span>
                <span className="edu-supervisor-role">{degree.supervisor.role}</span>
              </div>
              {degree.supervisor.linkedin && (
                <a
                  href={degree.supervisor.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="edu-supervisor-link"
                >
                  <span>LinkedIn Profile</span>
                  <IconArrowUpRight />
                </a>
              )}
            </div>
            <p className="edu-supervisor-quote">
              “{degree.supervisor.testimonial}”
            </p>
          </div>

          {/* Lecturers & Academic Mentors */}
          <div className="edu-people-group">
            <div className="edu-people-group-title">
              Lecturers, Professors & Mentors — SLIIT Northern UNI
            </div>
            <div className="edu-chips-grid">
              {degree.mentors.map((mentor) => (
                mentor.linkedin ? (
                  <a
                    key={mentor.name}
                    href={mentor.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="edu-chip-person"
                  >
                    <span>{mentor.name}</span>
                    <IconArrowUpRight />
                  </a>
                ) : (
                  <span key={mentor.name} className="edu-chip-person">
                    <span>{mentor.name}</span>
                  </span>
                )
              ))}
            </div>
          </div>

          {/* Seniors & Pillars of Support */}
          <div className="edu-people-group" style={{ marginBottom: 0 }}>
            <div className="edu-people-group-title">
              Seniors & Pillars of Guidance
            </div>
            <div className="edu-chips-grid">
              {degree.seniors.map((senior) => (
                <a
                  key={senior.name}
                  href={senior.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="edu-chip-person"
                >
                  <span>{senior.name}</span>
                  <span className="edu-chip-role">({senior.role})</span>
                  <IconArrowUpRight />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Complete Academic Timeline & University Degrees ──── */}
      <div className="edu-timeline-shell reveal">
        <div className="edu-timeline-top">
          <span className="edu-timeline-kicker">ACADEMIC CHRONOLOGY & DEGREES</span>
          <h3 className="edu-timeline-title">University Degrees & Academic Timeline</h3>
          <p className="edu-timeline-subtitle">
            Comprehensive academic history, institutional degrees, campus leadership, and engineering foundations across University of Bedfordshire, SLIIT Northern University, IIT Campus, and St. John's College.
          </p>
        </div>

        <div className="exp-wrapper">
          {education.map((edu, index) => {
            const isFirstClass = edu.isFirstClass || edu._id === 'edu1';
            return (
              <div
                key={edu._id || index}
                className={`exp-item ${isFirstClass ? 'first-class-item' : ''}`}
              >
                <div className="exp-sidebar">
                  <div className="exp-sidebar-header">
                    <div className="exp-num">{String(index + 1).padStart(2, '0')}</div>
                    {edu.logo && (
                      <img src={edu.logo} alt={`${edu.institution} logo`} className="exp-logo" />
                    )}
                  </div>
                  <div>
                    <div className="exp-co">{edu.institution}</div>
                    {edu.campus && (
                      <div className="edu-campus-tag">{edu.campus}</div>
                    )}
                    <div className="exp-period">{edu.period}</div>
                    {isFirstClass ? (
                      <span className="edu-status-pill gold">
                        <IconStar />
                        <span>First Class Honours</span>
                      </span>
                    ) : (
                      <div className="exp-type-tag">Education</div>
                    )}
                  </div>
                </div>

                <div className="exp-body">
                  <div className="exp-role-title">{edu.degree}</div>
                  <ul className="exp-blist">
                    <li>{edu.description}</li>
                    {edu.achievements && edu.achievements.map((ach, i) => (
                      <li key={i}>{ach}</li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Full-Resolution Modal */}
      {modalOpen && (
        <DegreeLightbox
          image={degree.documentImage}
          onClose={() => setModalOpen(false)}
        />
      )}
    </section>
  );
};

export default Education;
